import {
	API_ENDPOINTS,
	customToast,
	DataColumnInterface,
	DataTable,
	DateFormats,
	formatDateFns,
	FormInput,
	Icon,
	Icons,
	Modal,
	ModalSizes,
	Permissions,
	replaceKeyWithValue,
	route,
	SelectOption,
	StringHelpers,
	useAuthorisationContext,
	useDisabledContext,
	useForm,
	useLangContext,
	Variants,
} from 'carrier-fe';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ToshibaWarrantyOverrideType } from '../../../../types/toshiba-warranty-overrides';

const NOTES_TRUNCATE_LIMIT = 100;
const MAX_NOTES_LENGTH = 255;

interface ToshibaWarrantyOverridesListProps {
	companyName?: string;
	onChange: () => void;
}

const defaultCreateData = {
	toshiba_warranty_level_id: '',
	expires_at: '',
	notes: '',
};

function ToshibaWarrantyOverridesList({ companyName, onChange }: ToshibaWarrantyOverridesListProps) {
	// Hooks and Contexts
	const { fields, crud } = useLangContext();
	const { hasPermissionAccess } = useAuthorisationContext();
	const { disabled, setDisabled } = useDisabledContext();
	const companyId = useParams().companyId;

	// State Management
	const [activeModal, setActiveModal] = useState<'create' | 'archive' | null>(null);
	const [warrantyLevels, setWarrantyLevels] = useState<SelectOption[]>([]);
	const [refetchKey, setRefetchKey] = useState(0);
	const [selectedOverride, setSelectedOverride] = useState<ToshibaWarrantyOverrideType | null>(null);

	// Form Setup
	const submitOverride = async (data: typeof defaultCreateData) => {
		return axios.post(
			route(API_ENDPOINTS.ADMIN.TOSHIBA_WARRANTY_OVERRIDE.STORE, { company: companyId || '' }),
			data
		);
	};

	const {
		store,
		setValues,
		Submit,
		isSuccess,
		setIsSuccess,
		Input,
		errors,
		data,
	} = useForm(submitOverride, defaultCreateData);

	// Effects
	useEffect(() => {
		if (activeModal === 'create') {
			fetchWarrantyLevels();
		}
	}, [activeModal]);

	useEffect(() => {
		if (isSuccess) {
			customToast({
				title: data.message,
				variant: Variants.Success,
			});
			setIsSuccess(false);
			setRefetchKey(prev => prev + 1);
			onChange();
			handleCloseCreateModal();
		}
	}, [isSuccess, data]);

	// Handlers
	const fetchWarrantyLevels = async () => {
		try {
			const response = await axios.get(
				route(API_ENDPOINTS.GENERIC.SELECT.TOSHIBA_WARRANTY_LEVELS)
			);
			setWarrantyLevels(response.data.data);
		} catch (err: any) {
			customToast({
				title: err?.response?.data?.message || 'Error loading warranty levels',
				variant: Variants.Danger,
			});
		}
	};

	const handleOptionSelect = (action: any, data: ToshibaWarrantyOverrideType) => {
		const actions: Record<string, () => void> = {
			archive: () => {
				setSelectedOverride(data);
				setActiveModal('archive');
			},
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	const handleCloseCreateModal = () => {
		setActiveModal(null);
		setValues(defaultCreateData);
	};

	const handleCloseArchiveModal = () => {
		setActiveModal(null);
		setSelectedOverride(null);
	};

	const archiveOverride = async () => {
		if (!selectedOverride?.id) return;

		setDisabled(true);
		try {
			const response = await axios.delete(
				route(API_ENDPOINTS.ADMIN.TOSHIBA_WARRANTY_OVERRIDE.DELETE, {
					toshibaWarrantyOverride: selectedOverride?.id,
				})
			);
			customToast({
				title: response?.data?.message || 'Warranty override archived successfully.',
				variant: Variants.Success,
			});
			onChange();
			setRefetchKey(prev => prev + 1);
		} catch (err: any) {
			customToast({
				title: err?.response?.data?.message || 'Error archiving warranty override.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			handleCloseArchiveModal();
		}
	};

	const items = useMemo(() => {
		const i = [];

		if (hasPermissionAccess(Permissions.TOSHIBA_WARRANTY_OVERRIDE_STORE)) {
			i.push({
				label: StringHelpers.title(
					crud?.buttons?.create?.default || 'Create'
				),
				disabled: false,
				onClick: () => setActiveModal('create'),
				icon: <Icon icon={Icons.ADD} />,
			});
		}

		return i;
	}, [crud, hasPermissionAccess]);

	const colMapping = (data: ToshibaWarrantyOverrideType): DataColumnInterface[] => {
		return [
			{
				id: 'warranty_level_name',
				label: StringHelpers.title(
					fields?.warranty_level
					|| 'Warranty level'
				),
				value: data.toshiba_warranty_level?.name || '-',
				sortable: true,
			},
			{
				id: 'created_at',
				label: StringHelpers.title(
					fields?.override_from
					|| 'Override from'
				),
				value: formatDateFns(
					data.created_at,
					DateFormats.DATETIME_SHORT
				),
				sortable: true,
			},
			{
				id: 'expires_at',
				label: StringHelpers.title(
					fields?.override_expiry
					|| 'Override expiry'
				),
				value: formatDateFns(
					data.expires_at,
					DateFormats.DATE
				),
				sortable: true,
			},
			{
				id: 'notes',
				label: StringHelpers.title(
					fields?.notes
					|| 'Notes'
				),
				value: data.notes
					? StringHelpers.limit(data.notes, NOTES_TRUNCATE_LIMIT)
					: '-',
			},
			{
				id: 'actions',
				label: StringHelpers.title(
					fields?.actions
					|| 'Actions'
				),
				value: (
					<FormInput
						type='select'
						label={StringHelpers.title(
							fields?.actions
							|| 'Action'
						)}
						placeholder={StringHelpers.title(
							fields?.select
							|| 'Select'
						)}
						name='actions'
						value=''
						disabled={disabled}
						onChange={(value) => {
							if (!!value && typeof value === 'string') {
								handleOptionSelect(value, data);
							}
						}}
						options={[
							...hasPermissionAccess(Permissions.TOSHIBA_WARRANTY_OVERRIDE_DELETE) ? [{
								label: StringHelpers.title(
									crud?.select_labels?.archive
									|| 'Archive'
								),
								value: 'archive',
							}] : [],
						]}
						noMargin
					/>
				),
			},
		];
	};

	return (
		<>
			<DataTable<ToshibaWarrantyOverrideType>
				refreshOnChange={String(refetchKey)}
				title={StringHelpers.title(
					crud?.models?.toshiba_warranty_override
					|| 'Warranty Overrides'
				)}
				routeEndpoint={route(
					API_ENDPOINTS.ADMIN.TOSHIBA_WARRANTY_OVERRIDE.INDEX,
					{...companyId && { company: companyId }}
				)}
				colMapping={colMapping}
				items={items}
				incrementPaginationButtons
				variant={Variants.Info}
				prefix='company_view_warranty_overrides'
			/>

			<Modal
				open={activeModal === 'create'}
				onClose={handleCloseCreateModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.pages?.create?.title || 'Create :model',
						'model',
						crud?.models?.toshiba_warranty_override || 'Warranty Override'
					)
				)}
				disabled={disabled}
				size={ModalSizes.Large}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				overriddenConfirmButton={
					<Submit
						defaultLabel={crud?.buttons?.create?.default || 'Create'}
						loadingLabel={crud?.buttons?.create?.submitting || 'Creating...'}
						variant={Variants.Info}
					/>
				}
			>
				<FormInput
					type='text'
					name='company'
					label={StringHelpers.title(fields?.company || 'Company')}
					value={companyName || ''}
					disabled={true}
				/>

				{Input({
					name: 'toshiba_warranty_level_id',
					label: StringHelpers.title(fields?.warranty_level || 'Warranty Level'),
					type: 'searchable-select',
					options: warrantyLevels,
				})}

				<FormInput
					type='date'
					name='expires_at'
					label={StringHelpers.title(fields?.override_expiry || 'Expiry')}
					value={store?.expires_at || ''}
					onChange={(value) =>
						setValues({
							expires_at: value === '__/__/____' ? '' : (value as string),
						})
					}
					errorMessages={errors?.expires_at}
					disabled={disabled}
				/>

				{Input({
					name: 'notes',
					label: StringHelpers.title(fields?.notes || 'Notes'),
					type: 'textarea',
					description: `${MAX_NOTES_LENGTH - (store?.notes?.length || 0)} characters remaining`,
				})}
			</Modal>

			<Modal
				open={activeModal === 'archive'}
				onClose={handleCloseArchiveModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.archive?.title || 'Archive :name?',
						'name',
						crud?.models?.toshiba_warranty_override || 'Warranty Override'
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.archive?.submitting || 'Archiving...'
						: crud?.buttons?.archive?.default || 'Archive'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={archiveOverride}
				disabled={disabled}
				variant={Variants.Danger}
			>
				{crud?.modals?.archive?.description?.map(
					(item: string, key: number) => (
						<p key={key}>
							{replaceKeyWithValue(
								item,
								'name',
								'this'
							)}
						</p>
					)
				)}

				<div style={{ marginTop: '1rem' }}>
					<p>
						<strong>{StringHelpers.title(fields?.company || 'Company')}:</strong>{' '}
						{companyName || '-'}
					</p>
					<p>
						<strong>{StringHelpers.title(fields?.warranty_level || 'Warranty Level')}:</strong>{' '}
						{selectedOverride?.toshiba_warranty_level?.name || '-'}
					</p>
					<p>
						<strong>{StringHelpers.title(fields?.override_expiry || 'Override Expiry Date')}:</strong>{' '}
						{formatDateFns(selectedOverride?.expires_at || null, DateFormats.DATE)}
					</p>
				</div>
			</Modal>
		</>
	);
}

export default ToshibaWarrantyOverridesList;
